/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for bulk operation execution with progress tracking
 */

"use client";

import {
  estimateTimeRemaining,
  generateOperationId,
  updateProgressPercentage,
} from "@/lib/utils/domain/bulk-operations";
import { logger } from "@sentry/nextjs";
import { useCallback, useState } from "react";
import type {
  BulkOperationDefinition,
  BulkOperationMode,
  BulkOperationStatus,
  BulkProgressState,
} from "./types";

interface UseBulkOperationExecutionOptions<T> {
  availableOperations: BulkOperationDefinition<T>[];
  selectedItems: T[];
  keyExtractor: (item: T) => string;
  bulkProgress: BulkProgressState;
  setBulkProgress: React.Dispatch<React.SetStateAction<BulkProgressState>>;
  isPaused: boolean;
  setIsPaused: (paused: boolean) => void;
  setSelectedIds: (ids: Set<string>) => void;
  onSelectionChange?: (ids: Set<string>, items: T[]) => void;
  onOperationComplete?: (
    operationId: string,
    status: BulkOperationStatus,
    summary: {
      completed: number;
      failed: number;
      failures: Array<{ itemId: string; error: string; timestamp: Date }>;
      duration: number;
    },
  ) => void;
  addUndoOperation: (
    operationId: string,
    type: BulkOperationMode,
    affectedItems: T[],
    undoTimeLimit?: number,
  ) => void;
}

/**
 * Hook for executing bulk operations with progress tracking
 */
export function useBulkOperationExecution<T>({
  availableOperations,
  selectedItems,
  keyExtractor,
  bulkProgress,
  setBulkProgress,
  isPaused,
  setIsPaused,
  setSelectedIds,
  onSelectionChange,
  onOperationComplete,
  addUndoOperation,
}: UseBulkOperationExecutionOptions<T>) {
  // Operation control state
  const [operationController, setOperationController] =
    useState<AbortController | null>(null);

  // Execute operation with enhanced tracking
  const executeOperation = useCallback(
    async (operationId: string) => {
      logger.debug("executeOperation called", {
        operationId,
        availableOperationsCount: availableOperations.length,
        availableOperationIds: availableOperations.map((op) => op.id),
        selectedItemsCount: selectedItems.length,
      });
      const operation = availableOperations.find((op) => op.id === operationId);
      logger.debug("Operation found", { found: !!operation });
      if (!operation || selectedItems.length === 0) {
        logger.debug("Returning early", {
          hasOperation: !!operation,
          hasItems: selectedItems.length > 0,
        });
        return;
      }

      const controller = new AbortController();
      setOperationController(controller);

      const opId = generateOperationId();
      const startTime = new Date();
      const total = selectedItems.length;
      const batchSize = operation.batchSize ?? 1;

      setBulkProgress({
        operationId: opId,
        mode: operationId as BulkOperationMode,
        status: "running",
        completed: 0,
        total,
        failed: 0,
        failures: [],
        startedAt: startTime,
        completedAt: null,
        canPause: batchSize === 1, // Can pause single-item operations
        canCancel: true,
        percentage: 0,
        estimatedTimeRemaining: null,
      });

      let completed = 0;
      let failed = 0;
      const failures: Array<{
        itemId: string;
        error: string;
        timestamp: Date;
      }> = [];

      try {
        // Process in batches
        for (let i = 0; i < selectedItems.length; i += batchSize) {
          if (controller.signal.aborted) {
            setBulkProgress((prev) => ({ ...prev, status: "cancelled" }));
            return;
          }

          // Wait if paused
          while (isPaused && !controller.signal.aborted) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          const batch = selectedItems.slice(i, i + batchSize);

          try {
            await operation.handler(batch, (batchCompleted) => {
              const totalCompleted = completed + batchCompleted;
              const percentage = updateProgressPercentage(
                totalCompleted,
                total,
              );
              const estimatedTime = estimateTimeRemaining(
                startTime,
                totalCompleted,
                total,
              );

              setBulkProgress((prev) => ({
                ...prev,
                completed: totalCompleted,
                percentage,
                estimatedTimeRemaining: estimatedTime,
              }));
            });

            completed += batch.length;
          } catch (error) {
            failed += batch.length;
            batch.forEach((item) => {
              failures.push({
                itemId: keyExtractor(item),
                error: error instanceof Error ? error.message : "Unknown error",
                timestamp: new Date(),
              });
            });
          }

          // Update progress
          const percentage = updateProgressPercentage(completed, total);
          const estimatedTime = estimateTimeRemaining(
            startTime,
            completed,
            total,
          );

          setBulkProgress((prev) => ({
            ...prev,
            completed,
            failed,
            failures,
            percentage,
            estimatedTimeRemaining: estimatedTime,
          }));
        }

        const status: BulkOperationStatus =
          failed === 0 ? "completed" : "failed";
        const completedAt = new Date();

        setBulkProgress((prev) => ({
          ...prev,
          status,
          completedAt,
          canPause: false,
          canCancel: false,
          estimatedTimeRemaining: null,
        }));

        // Add to undo history if operation supports it
        if (operation.canUndo && failed === 0) {
          addUndoOperation(
            opId,
            operationId as BulkOperationMode,
            selectedItems,
            operation.undoTimeLimit,
          );
        }

        // Notify completion
        onOperationComplete?.(opId, status, {
          completed,
          failed,
          failures,
          duration: completedAt.getTime() - startTime.getTime(),
        });

        // Clear selection after successful operation
        if (status === "completed") {
          setSelectedIds(new Set());
          onSelectionChange?.(new Set(), []);
        }
      } catch (error) {
        setBulkProgress((prev) => ({
          ...prev,
          status: "failed",
          completedAt: new Date(),
          canPause: false,
          canCancel: false,
        }));
        throw error;
      } finally {
        setOperationController(null);
        setIsPaused(false);

        // Clear progress after delay
        setTimeout(() => {
          setBulkProgress((prev) => ({
            ...prev,
            operationId: null,
            mode: null,
            status: "pending",
          }));
        }, 3000);
      }
    },
    [
      availableOperations,
      selectedItems,
      isPaused,
      keyExtractor,
      addUndoOperation,
      onOperationComplete,
      onSelectionChange,
      setBulkProgress,
      setSelectedIds,
      setIsPaused,
    ],
  );

  // Pause operation
  const pauseOperation = useCallback(() => {
    if (bulkProgress.canPause && bulkProgress.status === "running") {
      setIsPaused(true);
      setBulkProgress((prev) => ({ ...prev, status: "paused" }));
    }
  }, [
    bulkProgress.canPause,
    bulkProgress.status,
    setIsPaused,
    setBulkProgress,
  ]);

  // Resume operation
  const resumeOperation = useCallback(() => {
    if (isPaused && bulkProgress.status === "paused") {
      setIsPaused(false);
      setBulkProgress((prev) => ({ ...prev, status: "running" }));
    }
  }, [isPaused, bulkProgress.status, setIsPaused, setBulkProgress]);

  // Cancel operation
  const cancelOperation = useCallback(() => {
    if (operationController && bulkProgress.canCancel) {
      operationController.abort();
      setBulkProgress((prev) => ({ ...prev, status: "cancelled" }));
    }
  }, [operationController, bulkProgress.canCancel, setBulkProgress]);

  return {
    executeOperation,
    pauseOperation,
    resumeOperation,
    cancelOperation,
  };
}
